import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../hooks/useLocale';

const LanguageSelector: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentLocale, changeLanguage, supportedLocales } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languageLabels: Record<string, { flag: string; name: string }> = {
    en: { flag: '🇺🇸', name: 'English' },
    es: { flag: '🇪🇸', name: 'Español' },
  };

  const handleLanguageChange = (locale: 'en' | 'es') => {
    changeLanguage(locale);
    setIsOpen(false);
  };

  return (
    <div className='language-selector' ref={dropdownRef}>
      <button
        className='language-selector-trigger'
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup='true'
        aria-label={t('languageSelector.label')}
      >
        <span className='language-flag'>
          {languageLabels[currentLocale].flag}
        </span>
        <span className='language-code'>{currentLocale.toUpperCase()}</span>
        <svg
          className={`dropdown-chevron ${isOpen ? 'open' : ''}`}
          width='16'
          height='16'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
        >
          <path d='M6 9l6 6 6-6' />
        </svg>
      </button>

      {isOpen && (
        <div className='language-selector-menu'>
          {supportedLocales.map((locale) => (
            <button
              key={locale}
              className={`language-option ${locale === currentLocale ? 'active' : ''}`}
              onClick={() => handleLanguageChange(locale)}
            >
              <span className='language-flag'>{languageLabels[locale].flag}</span>
              <span className='language-name'>{languageLabels[locale].name}</span>
              {locale === currentLocale && (
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                >
                  <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
